import { useFieldArray, useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { Input } from "@/src/components/ui/input";
import { TrashIcon, PlusIcon } from "lucide-react";
import { useToast } from "@/src/components/ui/use-toast";
import { Dispatch, SetStateAction, useCallback, useEffect, useState } from "react";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "../../components/ui/form";
import { Button } from "@/src/components/ui/button";
import { useUserEditMutation } from "@/src/lib/userEditMutation";
import { CustomerType } from "./model";

const userDataSchema = z.object({
  firstName: z.string().min(1, "First name is required"),
  lastName: z.string().min(1, "Surname is required"),
  mobile: z
    .string()
    .trim()
    .regex(/^[0-9+\-()\s]+$/, "Invalid phone number"),
  email: z.string().min(1, "E-mail is required"),
  additionalEmails: z.array(z.string()),
  streetAddress: z.string().optional(),
  postalCode: z.string().optional(),
  city: z.string().optional(),
  stateRegion: z.string().optional(),
  countryRegion: z.string().optional(),
});

type CustomerEmailForm = z.infer<typeof userDataSchema>;

const EditCustomer = ({
  customer,
  setShouldRefetch,
}: {
  customer: CustomerType;
  setShouldRefetch: Dispatch<SetStateAction<boolean>>;
}) => {
  const { firstName, lastName, mobile, email, additionalEmails } = customer;
  const {
    streetAddress = "",
    postalCode = "",
    city = "",
    stateRegion = "",
    countryRegion = "",
  } = (customer?.hubspotAddress ?? {})

  const [customerModalOpen, setCustomerModalOpen] = useState(false);
  const emails = additionalEmails ? additionalEmails.map((e) => e.email) : [];
  const customerData = {
    firstName,
    lastName,
    mobile,
    email,
    emails,
    streetAddress,
    postalCode,
    city,
    stateRegion,
    countryRegion
  };
  const [showConfirmModal, setShowConfirmModal] = useState(false);

  const editUserMutation = useUserEditMutation(
    setCustomerModalOpen,
    setShowConfirmModal,
    customer.id as string,
    setShouldRefetch,
  );

  const form = useForm<CustomerEmailForm>({
    resolver: zodResolver(userDataSchema),
    defaultValues: customerData,
  });
  const { control } = form;
  const { toast } = useToast();

  const { fields, append, remove } = useFieldArray({
    control,
    name: "additionalEmails" as never,
  });

  const onSubmit = useCallback(
    (force = false) => {
      return form.handleSubmit(
        (data) => {
          const toastId = toast({
            description: "Updating customer email...",
            variant: "default",
            duration: Infinity,
          }).id;

          editUserMutation.mutate({ ...data, toastId, force });
        },
        () => {
          console.error("FORM ERROR", form.formState.errors, form.getValues());
        },
      )();
    },
    [form],
  );

  useEffect(() => {
    if (customerModalOpen) {
      form.reset({
        firstName: customerData.firstName,
        lastName: customerData.lastName,
        mobile: customerData.mobile,
        email: customerData.email,
        additionalEmails: customerData.emails ?? [],
        streetAddress: customerData.streetAddress ?? "",
        postalCode: customerData.postalCode ?? "",
        city: customerData.city ?? "",
        stateRegion: customerData.stateRegion ?? "",
        countryRegion: customerData.countryRegion ?? ""
      });
    } else {
      setShowConfirmModal(false);
    }
  }, [customerModalOpen]);

  return (
    <>
      <Button
        onClick={() => {
          console.log("click");
          setCustomerModalOpen(true);
        }}
        variant="link"
      >
        Edit
      </Button>
      <Dialog open={customerModalOpen} onOpenChange={setCustomerModalOpen}>
        <DialogContent className={`sm:max-w-[720px] max-h-[calc(100%-80px)] overflow-y-auto`}>
          <DialogHeader>
            <DialogTitle className="text-2xl flex flex-col">Edit customer</DialogTitle>
          </DialogHeader>
          {!showConfirmModal && (
            <Form {...form}>
              <form onSubmit={() => onSubmit()}>
                <div className="gap-4">
                  <FormField
                    name="firstName"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-slate-600">Name</FormLabel>
                        <FormControl>
                          <Input type="text" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <div className="mt-4">
                    <FormField
                      name="lastName"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="text-slate-600">Surname</FormLabel>
                          <FormControl>
                            <Input type="text" {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>

                  <div className="mt-4">
                    <FormField
                      name="email"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="text-slate-600">E-mail</FormLabel>
                          <FormControl>
                            <Input type="text" {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>
                  <div className="mt-4 space-y-4">
                    {fields.length > 0 && (
                      <>
                        <div className="text-sm bg-blue-100 w-full h-[1px] mt-6 mb-4" />
                        <FormLabel className="text-slate-600">Additonal e-mails</FormLabel>
                        {fields.map((field, index) => (
                          <FormField
                            key={field.id}
                            control={control}
                            name={`additionalEmails.${index}`}
                            render={({ field }) => (
                              <FormItem className="flex items-center gap-2">
                                <FormControl>
                                  <Input placeholder={`additional email`} type="text" {...field} />
                                </FormControl>
                                <Button type="button" variant="outline" className="!mt-0" onClick={() => remove(index)}>
                                  <TrashIcon size={16} />
                                </Button>
                              </FormItem>
                            )}
                          />
                        ))}
                      </>
                    )}

                    <Button
                      onClick={() => {
                        append("");
                      }}
                      className="col-start-1 text-sm"
                      type="button"
                      variant="secondary"
                    >
                      Add additonal email
                      <PlusIcon className="ml-2 w-4 h-4 stroke-slate-900" />
                    </Button>
                  </div>
                  <div className="mt-4">
                    <FormField
                      name="mobile"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="text-slate-600">Phone number</FormLabel>
                          <FormControl>
                            <Input type="text" {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>
                  <div className="mt-4">
                    <FormField
                      name="streetAddress"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="text-slate-600">Street address</FormLabel>
                          <FormControl>
                            <Input type="text" {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>
                  <div className="mt-4">
                    <FormField
                      name="postalCode"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="text-slate-600">Postal code</FormLabel>
                          <FormControl>
                            <Input type="text" {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>
                  <div className="mt-4">
                    <FormField
                      name="city"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="text-slate-600">City</FormLabel>
                          <FormControl>
                            <Input type="text" {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>
                  <div className="mt-4">
                    <FormField
                      name="stateRegion"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="text-slate-600">State/Region</FormLabel>
                          <FormControl>
                            <Input type="text" {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>
                  <div className="mt-4">
                    <FormField
                      name="countryRegion"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="text-slate-600">Country/Region</FormLabel>
                          <FormControl>
                            <Input type="text" {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>
                </div>
              </form>
            </Form>
          )}
          {showConfirmModal && (
            <div className="flex flex-col gap-6">
              <p className="text-slate-700">
                A user with this email ({form.getValues("email")}) already exists. Please use a unique email address.
              </p>
            </div>
          )}
          <div className="-mx-6 mt-2 mb-2">
            <hr className="border-t border-gray-200 w-full" />
          </div>
          <DialogFooter>
            {!showConfirmModal && (
              <Button onClick={() => onSubmit()} type="submit">
                Save
              </Button>
            )}
            {showConfirmModal && (
              <>
                <Button
                  className="mr-auto"
                  variant="secondary"
                  onClick={() => {
                    setShowConfirmModal(false);
                  }}
                >
                  Cancel
                </Button>
                <Button
                  onClick={() => {
                    setShowConfirmModal(false);
                  }}
                  type="submit"
                >
                  Confirm
                </Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default EditCustomer;
